import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// Importing for the side effects: ``registerTour`` runs at module load.
import { RFIS_DETAIL_TOUR_ID } from '@/features/tours/rfis/detail';
import { RFIS_COMBINED_TOUR_ID } from '@/features/tours/rfis/combined';
import { useTour } from '@/features/tours';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { useAllUserProjects } from '@/shared/hooks/useUserProjects';
import {
  ArrowLeft,
  Crosshair,
  Paperclip,
  Send,
  Printer,
  FileText,
  HelpCircle,
  MessageSquare,
  Users,
  History,
  ClipboardList,
  Calendar,
  CheckCircle,
  Clock,
  Circle,
  MoreHorizontal,
  Pencil,
} from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Textarea } from '@/shared/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { RfiFormDialog } from './RfiFormDialog';
import { useProjectUsers, useRFI, useRFIMutations } from '../hooks';
import {
  RFI_PRIORITY_LABELS,
  RFI_STATUS_LABELS,
  RFI_IMPACT_LABELS,
  type RFIStatus,
} from '../types';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const STATUS_BADGE: Record<RFIStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  OPEN: 'bg-blue-100 text-blue-800',
  PENDING_RESPONSE: 'bg-amber-100 text-amber-800',
  ANSWERED: 'bg-green-100 text-green-800',
  CLOSED: 'bg-slate-100 text-slate-700',
};

const PRIORITY_BADGE: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-700',
  NORMAL: 'bg-blue-100 text-blue-800',
  HIGH: 'bg-orange-100 text-orange-800',
  URGENT: 'bg-red-100 text-red-800',
};

const STATUS_BANNER: Record<
  RFIStatus,
  { wrap: string; icon: string; text: string; Icon: typeof CheckCircle }
> = {
  DRAFT: { wrap: 'border-gray-200 bg-gray-50', icon: 'text-gray-600', text: 'text-gray-800', Icon: FileText },
  OPEN: { wrap: 'border-blue-200 bg-blue-50', icon: 'text-blue-600', text: 'text-blue-800', Icon: Circle },
  PENDING_RESPONSE: {
    wrap: 'border-amber-200 bg-amber-50',
    icon: 'text-amber-600',
    text: 'text-amber-800',
    Icon: Clock,
  },
  ANSWERED: {
    wrap: 'border-green-200 bg-green-50',
    icon: 'text-green-600',
    text: 'text-green-800',
    Icon: CheckCircle,
  },
  CLOSED: { wrap: 'border-slate-200 bg-slate-50', icon: 'text-slate-600', text: 'text-slate-800', Icon: CheckCircle },
};

const BALL_IN_COURT_LABELS: Record<string, string> = {
  responsible_party: 'Responsible Party',
  requested_by: 'Requested By',
  closed: 'Closed',
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const userFullName = (u: { first_name: string; last_name: string } | null | undefined) => {
  if (!u) return '—';
  return `${u.first_name} ${u.last_name}`.trim() || '—';
};

export const RfiDetail: React.FC = () => {
  const { id, clientName } = useParams<{ id: string; clientName?: string }>();
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = location.pathname.replace(/\/[^/]+$/, '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: item, isLoading } = useRFI(id, numericProjectId);
  const { data: users = [] } = useProjectUsers(numericProjectId);
  const { allProjects } = useAllUserProjects(clientName);
  const projectName = allProjects.find((p) => p.id === numericProjectId)?.name ?? '';
  const {
    submitItem,
    assignItem,
    respondItem,
    closeItem,
    reopenItem,
    addComment,
    uploadAttachment,
    deleteAttachment,
  } = useRFIMutations(numericProjectId);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [officialResponseDraft, setOfficialResponseDraft] = useState('');

  // Auto-start the detail-view guided tour once the RFI has loaded.
  // Skipped if a tour is already active (the combined tour navigated us
  // here) or if the user has already seen the combined tour.
  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (!item) return;
    if (hasSeen(RFIS_COMBINED_TOUR_ID)) return;
    void startTour(RFIS_DETAIL_TOUR_ID);
  }, [tourHydrated, active, startTour, hasSeen, item]);

  if (isLoading || !item) {
    return (
      <BaseLayout>
        <div className="p-6 max-w-6xl mx-auto text-muted-foreground">Loading RFI...</div>
      </BaseLayout>
    );
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    await uploadAttachment.mutateAsync({ id, file });
    e.target.value = '';
  };

  const handlePrint = () => {
    const origin = window.location.origin;

    const fields: [string, string][] = [
      ['Status', RFI_STATUS_LABELS[item.status]],
      ['Priority', RFI_PRIORITY_LABELS[item.priority]],
      ['Due Date', formatDate(item.due_date)],
      ['Ball In Court', BALL_IN_COURT_LABELS[item.ball_in_court] ?? '—'],
      ['Requested By', userFullName(item.requested_by_user)],
      ['Responsible Party', userFullName(item.responsible_party_user)],
      ['Assignee', userFullName(item.assignee_user)],
      ['Trade', item.trade?.name ?? '—'],
      ['Location', item.location_reference?.name ?? '—'],
      ['Drawing', item.drawing_reference?.label ?? '—'],
      ['Spec Section', item.spec_section_reference || '—'],
      ['Cost Impact', RFI_IMPACT_LABELS[item.cost_impact]],
      ['Schedule Impact', RFI_IMPACT_LABELS[item.schedule_impact]],
      ['Created At', formatDateTime(item.created_at)],
      ['Response Date', formatDateTime(item.response_date)],
    ];

    const metaHtml = fields
      .map(
        ([label, value]) =>
          `<div><span class="label">${escapeHtml(label)}:</span> ${escapeHtml(value)}</div>`,
      )
      .join('');

    const sectionHtml = (title: string, body: string) =>
      `<div class="section"><h2>${escapeHtml(title)}</h2><p>${escapeHtml(body)}</p></div>`;

    const suggestedHtml = item.suggested_solution
      ? sectionHtml('Suggested Solution', item.suggested_solution)
      : '';

    const commentsHtml = item.comments.length
      ? `<div class="section"><h2>Comments / Activity</h2>${item.comments
          .map(
            (comment) =>
              `<p><strong>${escapeHtml(userFullName(comment.author))}</strong> <span class="muted">${escapeHtml(
                formatDateTime(comment.created_at),
              )}</span><br/>${escapeHtml(comment.body)}</p>`,
          )
          .join('')}</div>`
      : '';

    const historyHtml = item.status_history.length
      ? `<div class="section"><h2>Status History</h2>${item.status_history
          .map(
            (entry) =>
              `<p><strong>${escapeHtml(entry.from_status || 'Created')} → ${escapeHtml(
                entry.to_status,
              )}</strong> <span class="muted">${escapeHtml(formatDateTime(entry.created_at))}</span>${
                entry.note ? `<br/>${escapeHtml(entry.note)}` : ''
              }</p>`,
          )
          .join('')}</div>`
      : '';

    const attachmentsHtml = item.attachments.length
      ? `<div class="section"><h2>Attachments (${item.attachments.length})</h2><ul>${item.attachments
          .map((att) => `<li>${escapeHtml(att.file.split('/').pop() ?? att.file)}</li>`)
          .join('')}</ul></div>`
      : '';

    const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(item.rfi_number)} ${escapeHtml(item.title)}</title>
<style>
  @page { size: auto; margin: 0.6in; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 0; }
  .header { position: relative; text-align: center; border-bottom: 2px solid #111827; padding-bottom: 12px; margin-bottom: 18px; }
  .header .logo { position: absolute; top: 0; right: 0; height: 40px; width: auto; }
  .header .project { font-size: 13px; font-weight: 600; color: #374151; margin: 0 0 2px; }
  .header h1 { font-size: 24px; font-weight: 700; margin: 0 0 4px; }
  .header .subtitle { font-size: 12px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #6b7280; }
  .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 24px; font-size: 12px; margin-bottom: 20px; }
  .meta .label { color: #6b7280; font-weight: 600; }
  .section { border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 14px; margin-bottom: 12px; break-inside: avoid; page-break-inside: avoid; }
  .section h2 { font-size: 14px; font-weight: 700; margin: 0 0 8px; }
  .section p { font-size: 12px; margin: 0 0 8px; white-space: pre-wrap; }
  .section p:last-child { margin-bottom: 0; }
  .section .muted { color: #6b7280; font-weight: 400; }
  .section ul { margin: 0; padding-left: 18px; }
  .section li { font-size: 12px; }
</style>
</head>
<body>
  <div class="header">
    <img class="logo" src="${origin}/static/images/iv360-p-300.webp" alt="Ironview 360" />
    ${projectName ? `<div class="project">${escapeHtml(projectName)}</div>` : ''}
    <h1>${escapeHtml(item.title)}</h1>
    <div class="subtitle">RFI ${escapeHtml(item.rfi_number)} &middot; ${escapeHtml(RFI_STATUS_LABELS[item.status])}</div>
  </div>
  <div class="meta">${metaHtml}</div>
  ${sectionHtml('Question', item.question || 'No question provided.')}
  ${suggestedHtml}
  ${sectionHtml('Official Response', item.official_response || 'No official response yet.')}
  ${commentsHtml}
  ${historyHtml}
  ${attachmentsHtml}
  <script>
    window.onload = function () { window.focus(); window.print(); };
    window.onafterprint = function () { window.close(); };
  </script>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const banner = STATUS_BANNER[item.status];
  const BannerIcon = banner.Icon;
  const bannerMessage =
    item.status === 'DRAFT'
      ? 'Draft — not yet submitted'
      : item.status === 'ANSWERED'
        ? `Answered${item.response_date ? ` on ${formatDate(item.response_date)}` : ''}`
        : item.status === 'CLOSED'
          ? `Closed${item.closed_at ? ` on ${formatDate(item.closed_at)}` : ''}`
          : item.status === 'PENDING_RESPONSE'
            ? `Pending response${item.due_date ? ` — due ${formatDate(item.due_date)}` : ''}`
            : `Open${item.due_date ? ` — due ${formatDate(item.due_date)}` : ''}`;

  return (
    <BaseLayout>
      <div className="mx-auto w-full min-w-0 max-w-6xl space-y-4 break-words p-4 sm:space-y-6 md:p-6">
        {/* ── Header ────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => navigate(backPath)} aria-label="Back to RFIs">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <div className="flex min-w-0 flex-col items-start gap-0.5 sm:flex-row sm:gap-2">
                <span className="shrink-0 font-mono text-sm text-muted-foreground">{item.rfi_number}</span>
                <h1 className="min-w-0 break-words text-2xl font-bold tracking-tight md:text-3xl lg:truncate">
                  {item.title}
                </h1>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={PRIORITY_BADGE[item.priority]}>
                  {RFI_PRIORITY_LABELS[item.priority]} Priority
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Created {formatDateTime(item.created_at)}
                </span>
              </div>
            </div>
          </div>

          <div className="w-full flex-shrink-0 lg:w-auto" data-tour-id="rfi-detail-header-actions">
            <div className="hidden flex-wrap items-center gap-2 lg:flex">
              {item.hotspot_detail && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    const h = item.hotspot_detail!;
                    const params = new URLSearchParams({
                      photo: String(h.photo_id),
                      hotspot: String(h.id),
                      pitch: String(h.pitch),
                      yaw: String(h.yaw),
                    });
                    const base = location.pathname.replace(/\/rfis\/.*$/, '');
                    const viewerRoute = h.scan_type === 'Progress' ? 'progress' : 'asbuilds';
                    navigate(`${base}/${viewerRoute}?${params.toString()}`);
                  }}
                >
                  <Crosshair className="h-4 w-4" />
                  View Hotspot
                </Button>
              )}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handlePrint}>
                <Printer className="h-4 w-4" />
                Print PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsEditOpen(true)} disabled={!item.can_edit}>Edit</Button>
              {item.status === 'DRAFT' && (
                <Button size="sm" onClick={() => id && submitItem.mutate(id)}>Submit / Open</Button>
              )}
              {item.status !== 'CLOSED' && (
                <Button size="sm" variant="secondary" onClick={() => id && closeItem.mutate({ id })}>Close</Button>
              )}
              {item.status === 'CLOSED' && (
                <Button size="sm" variant="secondary" onClick={() => id && reopenItem.mutate(id)}>Reopen</Button>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-10 w-full gap-2 lg:hidden">
                  <MoreHorizontal className="h-4 w-4" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 max-w-[calc(100vw-2rem)]">
                {item.hotspot_detail && (
                  <DropdownMenuItem
                    onSelect={() => {
                      const h = item.hotspot_detail!;
                      const params = new URLSearchParams({
                        photo: String(h.photo_id),
                        hotspot: String(h.id),
                        pitch: String(h.pitch),
                        yaw: String(h.yaw),
                      });
                      const base = location.pathname.replace(/\/rfis\/.*$/, '');
                      const viewerRoute = h.scan_type === 'Progress' ? 'progress' : 'asbuilds';
                      navigate(`${base}/${viewerRoute}?${params.toString()}`);
                    }}
                  >
                    <Crosshair className="mr-2 h-4 w-4" />
                    View hotspot
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={handlePrint}>
                  <Printer className="mr-2 h-4 w-4" />
                  Print PDF
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setIsEditOpen(true)} disabled={!item.can_edit}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit RFI
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {item.status === 'DRAFT' && (
                  <DropdownMenuItem onSelect={() => id && submitItem.mutate(id)}>
                    <Send className="mr-2 h-4 w-4" />
                    Submit and open
                  </DropdownMenuItem>
                )}
                {item.status !== 'CLOSED' && (
                  <DropdownMenuItem onSelect={() => id && closeItem.mutate({ id })}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Close RFI
                  </DropdownMenuItem>
                )}
                {item.status === 'CLOSED' && (
                  <DropdownMenuItem onSelect={() => id && reopenItem.mutate(id)}>
                    <Circle className="mr-2 h-4 w-4" />
                    Reopen RFI
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* ── Status banner ─────────────────────────────────────────── */}
        <Card className={banner.wrap}>
          <CardContent className="p-4 sm:p-6 sm:py-4">
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-2 sm:items-center">
                <BannerIcon className={`h-5 w-5 shrink-0 ${banner.icon}`} />
                <span className={`min-w-0 break-words text-sm font-medium ${banner.text}`}>{bannerMessage}</span>
              </div>
              <div className="flex max-w-full flex-wrap items-center gap-2">
                <span className="hidden text-xs text-muted-foreground sm:inline">Ball in court:</span>
                <Badge variant="secondary" className="max-w-full whitespace-normal">{BALL_IN_COURT_LABELS[item.ball_in_court] ?? '—'}</Badge>
                <Badge className={`max-w-full whitespace-normal ${STATUS_BADGE[item.status]}`}>{RFI_STATUS_LABELS[item.status]}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Main content: 2/3 + 1/3 on desktop ───────────────────── */}
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
          {/* Left column */}
          <div className="min-w-0 space-y-4 sm:space-y-6 lg:col-span-2">
            {/* Details */}
            <Card data-tour-id="rfi-detail-metadata">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                  <div>
                    <dt className="font-medium text-muted-foreground">Priority</dt>
                    <dd className="mt-1">
                      <Badge variant="outline" className={PRIORITY_BADGE[item.priority]}>
                        {RFI_PRIORITY_LABELS[item.priority]}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Due Date
                    </dt>
                    <dd className={`mt-0.5 ${item.is_overdue ? 'font-semibold text-red-600' : ''}`}>
                      {formatDate(item.due_date)}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Responsible Party</dt>
                    <dd className="mt-0.5">{userFullName(item.responsible_party_user)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Assignee</dt>
                    <dd className="mt-0.5">{userFullName(item.assignee_user)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Requested By</dt>
                    <dd className="mt-0.5">{userFullName(item.requested_by_user)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Ball In Court</dt>
                    <dd className="mt-0.5">{BALL_IN_COURT_LABELS[item.ball_in_court] ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Cost Impact</dt>
                    <dd className="mt-0.5">{RFI_IMPACT_LABELS[item.cost_impact]}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Schedule Impact</dt>
                    <dd className="mt-0.5">{RFI_IMPACT_LABELS[item.schedule_impact]}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Location</dt>
                    <dd className="mt-0.5">{item.location_reference?.name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Drawing</dt>
                    <dd className="mt-0.5">{item.drawing_reference?.label || '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground">Spec Section</dt>
                    <dd className="mt-0.5">{item.spec_section_reference || '—'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created At
                    </dt>
                    <dd className="mt-0.5">{formatDateTime(item.created_at)}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            {/* Question */}
            <Card data-tour-id="rfi-detail-question">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  Question
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="whitespace-pre-wrap">{item.question || '—'}</p>
                {item.suggested_solution && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Suggested Solution</p>
                    <p className="mt-0.5 whitespace-pre-wrap">{item.suggested_solution}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Official Response */}
            <Card data-tour-id="rfi-detail-response">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Official Response
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm whitespace-pre-wrap">{item.official_response || 'No official response yet.'}</p>
                <p className="text-xs text-muted-foreground">Response Date: {formatDateTime(item.response_date)}</p>
                {item.status !== 'CLOSED' && (
                  <div className="space-y-2">
                    <Textarea
                      placeholder="Add official response..."
                      value={officialResponseDraft}
                      onChange={(e) => setOfficialResponseDraft(e.target.value)}
                    />
                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                      <Button
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() => id && respondItem.mutate({ id, official_response: officialResponseDraft })}
                        disabled={!officialResponseDraft.trim()}
                      >
                        Mark Answered
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full sm:w-auto"
                        onClick={() => id && closeItem.mutate({ id, official_response: officialResponseDraft })}
                        disabled={!officialResponseDraft.trim()}
                      >
                        Respond + Close
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Comments */}
            <Card data-tour-id="rfi-detail-comments">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Comments / Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {item.comments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No comments yet.</p>
                  ) : (
                    item.comments.map((comment) => (
                      <div key={comment.id} className="border rounded-md p-2">
                        <p className="text-xs text-muted-foreground">
                          {userFullName(comment.author)} • {formatDateTime(comment.created_at)}
                        </p>
                        <p className="text-sm whitespace-pre-wrap">{comment.body}</p>
                      </div>
                    ))
                  )}
                </div>
                {item.status !== 'CLOSED' && (
                  <>
                    <Textarea
                      placeholder="Add comment..."
                      value={commentBody}
                      onChange={(e) => setCommentBody(e.target.value)}
                    />
                    <Button
                      size="sm"
                      onClick={async () => {
                        if (!id || !commentBody.trim()) return;
                        await addComment.mutateAsync({ id, body: commentBody.trim() });
                        setCommentBody('');
                      }}
                    >
                      <Send className="mr-2 h-4 w-4" />
                      Add Comment
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card data-tour-id="rfi-detail-attachments">
              <CardHeader className="gap-3 p-4 pb-2 sm:flex-row sm:items-center sm:justify-between sm:p-6 sm:pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Paperclip className="h-5 w-5" />
                  Attachments ({item.attachments.length})
                </CardTitle>
                <Button size="sm" variant="outline" className="h-8 w-full sm:w-auto" onClick={() => fileInputRef.current?.click()}>
                  <Paperclip className="mr-1.5 h-3.5 w-3.5" />
                  Attach File
                </Button>
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {item.attachments.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No attachments yet.</p>
                  ) : (
                    item.attachments.map((attachment) => (
                      <div key={attachment.id} className="flex min-w-0 items-center justify-between gap-2 rounded-md border p-2">
                        <a
                          href={attachment.file_url ?? attachment.file}
                          target="_blank"
                          rel="noreferrer"
                          className="min-w-0 flex-1 truncate text-sm text-primary hover:underline"
                        >
                          {attachment.file.split('/').pop()}
                        </a>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="shrink-0"
                          onClick={() => id && deleteAttachment.mutate({ id, attachmentId: attachment.id })}
                        >
                          Remove
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div className="min-w-0 space-y-4 sm:space-y-6 lg:col-span-1">
            {/* Quick Assignment */}
            <Card data-tour-id="rfi-detail-assignment">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Quick Assignment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Responsible Party</label>
                  <Select
                    value={item.responsible_party_user ? String(item.responsible_party_user.id) : 'none'}
                    onValueChange={(value) => {
                      if (!id) return;
                      assignItem.mutate({
                        id,
                        payload: { responsible_party_user_id: value === 'none' ? undefined : Number(value) },
                      });
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Assignee</label>
                  <Select
                    value={item.assignee_user ? String(item.assignee_user.id) : 'none'}
                    onValueChange={(value) => {
                      if (!id) return;
                      assignItem.mutate({
                        id,
                        payload: { assignee_user_id: value === 'none' ? undefined : Number(value) },
                      });
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={String(u.id)}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Status History */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Status History
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {item.status_history.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No history yet.</p>
                ) : (
                  item.status_history.map((entry) => (
                    <div key={entry.id} className="text-sm border rounded-md p-2">
                      <p className="font-medium">{entry.from_status || 'Created'} → {entry.to_status}</p>
                      <p className="text-xs text-muted-foreground">{entry.note || 'Status updated'} • {formatDateTime(entry.created_at)}</p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Audit Trail */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Audit Trail
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {item.audit_logs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No audit entries yet.</p>
                ) : (
                  item.audit_logs.map((entry) => (
                    <div key={entry.id} className="text-sm border rounded-md p-2">
                      <p className="font-medium">{entry.message}</p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(entry.created_at)}</p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <RfiFormDialog
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        projectId={numericProjectId}
        item={item}
      />
    </BaseLayout>
  );
};
